import React, { FC, useCallback, useMemo, useState } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import Popover, { PopoverPlacement } from 'react-native-popover-view';
import styles from './SystemStatus.styles';
import theme from '../../../common/theme';
import SystemStatusRow, { STATES, SystemStatusAction } from './SystemStatusRow';
import Icon from '../../Icon/Icon';
import { useModal } from '@oolio-group/rn-use-modal';
import NetworkDevicesModal from '../NetworkDevicesModal/NetworkDevicesModal';
import ScreenSelectionModal from '../ScreenSelectionModal/ScreenSelectionModal';
import AndroidScreenSelectionModal from '../ScreenSelectionModal/AndroidScreenSelectionModal';
import Tap2PayDiagnosis from '../../Modals/Tap2PayDiagnosis/Tap2PayDiagnosis';
import ActionLogsModal from '../../Modals/ActionLogs/ActionLogsModal';
import { useLogout } from '../../../hooks/device/useLogout';
import useBehaviorSubjectState from '../../../hooks/rxjs/useSubjectState';
import { localCommsSubject } from '../../../state/localComms';
import { sessionSubject } from '../../../state/sessionObservable';
import { localComms } from '../../../utils/localComms';
import { isDesktop } from '../../../utils/environment';
import { isEmbedded } from '../../../embedded/embedded';

const SystemStatus: FC = () => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const { showModal, closeModal } = useModal();
  const { logout } = useLogout();
  const { value: isLocalCommsEligible } =
    useBehaviorSubjectState(localCommsSubject);
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  const isTap2PayEnabled =
    Platform.OS === 'android' &&
    !!session?.device?.enableTap2PayEmbedded &&
    !!session?.device?.linkCdsToPayApp;

  const onLogout = useCallback(() => {
    setShowOptions(false);
    logout();
  }, [logout]);

  const onToggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const showDevicesPopup = useCallback(() => {
    closeModal();
    setShowOptions(false);
    setTimeout(() => {
      showModal(<NetworkDevicesModal onClose={closeModal} />);
    }, 1000);
  }, [showModal, closeModal]);

  const showScreenSelectionModal = useCallback(() => {
    closeModal();
    setShowOptions(false);
    setTimeout(() => {
      showModal(<ScreenSelectionModal onClose={closeModal} />);
    }, 1000);
  }, [showModal, closeModal]);

  const showAndroidScreenSelectionModal = useCallback(() => {
    closeModal();
    setShowOptions(false);
    setTimeout(() => {
      showModal(<AndroidScreenSelectionModal onClose={closeModal} />);
    }, 1000);
  }, [showModal, closeModal]);

  const showTap2PayDiagnosis = useCallback(() => {
    closeModal();
    setShowOptions(false);
    const session = sessionSubject.getValue();
    const store = session?.currentStore;
    setTimeout(() => {
      showModal(
        <Tap2PayDiagnosis
          onClose={closeModal}
          payConfig={{
            merchantCode: store?.merchantCode,
            merchantId: store?.merchantId,
            storeRef: store?.payStoreRefs?.instore,
            deviceId: session?.device?.id,
            accessToken: session?.token,
          }}
        />,
      );
    }, 1000);
  }, [showModal, closeModal]);

  const showActionLogsModal = useCallback(() => {
    closeModal();
    setShowOptions(false);
    setTimeout(() => {
      showModal(<ActionLogsModal onClose={closeModal} />);
    }, 1000);
  }, [showModal, closeModal]);

  const onPressAction = useCallback((name: string, action?: () => void) => {
    action && action();
  }, []);

  const OPTIONS: SystemStatusAction[] = useMemo(() => {
    const actionLogs = {
      testID: 'btn-action-logs',
      label: 'Action Logs',
      type: STATES.NEUTRAL,
      action: showActionLogsModal,
    };
    if (isEmbedded()) return [actionLogs];

    let defaultOptions = [
      {
        testID: 'btn-logout',
        label: 'Logout',
        type: STATES.NEGATIVE,
        action: onLogout,
      },
    ];

    const additionalOptions = [actionLogs];

    if (isLocalCommsEligible) {
      additionalOptions.push({
        testID: 'status-devices',
        label: 'Devices',
        type: STATES.POSITIVE,
        action: showDevicesPopup,
      });
    }

    if (isDesktop() && localComms.toggleFullScreen) {
      additionalOptions.push({
        testID: 'btn-fullscreen',
        label: 'Toggle Fullscreen',
        type: STATES.NEUTRAL,
        action: async () => {
          setShowOptions(false);
          try {
            await localComms.toggleFullScreen?.();
          } catch (error) {
            console.error('Failed to toggle fullscreen:', error);
          }
        },
      });
    }

    if (isDesktop() && localComms.getAvailableScreens) {
      additionalOptions.push({
        testID: 'btn-select-screen',
        label: 'Select Screen',
        type: STATES.NEUTRAL,
        action: showScreenSelectionModal,
      });
    }

    if (Platform.OS === 'android') {
      additionalOptions.push({
        testID: 'btn-select-screen-android',
        label: 'Select Screen',
        type: STATES.NEUTRAL,
        action: showAndroidScreenSelectionModal,
      });
    }

    if (isTap2PayEnabled) {
      additionalOptions.push({
        testID: 'btn-diagnose-tap2pay',
        label: 'Diagnose Tap2Pay',
        type: STATES.NEUTRAL,
        action: showTap2PayDiagnosis,
      });
    }

    if (additionalOptions.length > 0) {
      defaultOptions = [...additionalOptions, ...defaultOptions];
    }

    return defaultOptions;
  }, [
    onLogout,
    showDevicesPopup,
    showScreenSelectionModal,
    showAndroidScreenSelectionModal,
    showTap2PayDiagnosis,
    isTap2PayEnabled,
    showActionLogsModal,
    isLocalCommsEligible,
  ]);

  return (
    <Popover
      from={
        <TouchableOpacity
          testID="btn-system"
          onPress={onToggleOptions}
          style={styles.button}
        >
          <Icon name="setting" color={theme.palette.white} />
        </TouchableOpacity>
      }
      isVisible={showOptions}
      placement={PopoverPlacement.BOTTOM}
      onRequestClose={() => setShowOptions(false)}
      backgroundStyle={styles.bgStyle}
      popoverStyle={styles.optionsContainer}
    >
      <View style={styles.optionsBg}>
        {OPTIONS.map(option => (
          <SystemStatusRow
            key={option.testID}
            testID={option.testID}
            type={option.type}
            label={option.label}
            action={() => {
              onPressAction(option.label, option.action);
            }}
          />
        ))}
      </View>
    </Popover>
  );
};

export default SystemStatus;
